import { useCompany } from '@upright/ui/api/model/input/companies';
import { getButtonBaseStyle, getButtonColorStyle } from '@upright/ui/common/generic/components/buttons/nice-button';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import {
  isAnonymouslyInterestedInCompany,
  saveInterestedAnonymouslyInCompany,
} from '@upright/ui/common/generic/utilities/storage';
import {
  useDeleteCompanyClientDataRequest,
  usePostCompanyClientDataRequest,
} from '@upright/ui/common/upright/company-client-data/query-hooks/reaction-query-hooks';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

interface Props {
  companyId: string;
  text: string;
  className?: string;
}

export function InterestedButton(props: Props) {
  const companyId = props.companyId;
  const companyQuery = useCompany(companyId);
  const company = companyQuery.data;
  const user = useGetLoggedInUser();
  const [clickedAnonymously, setClickedAnonymously] = useState<boolean>(isAnonymouslyInterestedInCompany(companyId));
  useEffect(() => {
    if (clickedAnonymously !== isAnonymouslyInterestedInCompany(companyId)) {
      saveInterestedAnonymouslyInCompany(clickedAnonymously, companyId);
    }
  }, [companyId, clickedAnonymously]);
  const postMutation = usePostCompanyClientDataRequest(companyId);
  const deleteMutation = useDeleteCompanyClientDataRequest(companyId);

  const userRequestedBefore: number = (() => {
    if (user) {
      return company?.userRequested ? 1 : 0;
    } else {
      return !postMutation.data && clickedAnonymously ? 1 : 0;
    }
  })();
  const othersRequestedBefore = (company?.numRequests || 0) - userRequestedBefore;
  const isClicked = !user ? clickedAnonymously : postMutation.data || (!deleteMutation.data && company?.userRequested);
  const numRequested =
    deleteMutation.data || postMutation.data
      ? othersRequestedBefore + (postMutation.data || 0)
      : company?.numRequests || 0;
  const interestedText = numRequested > 1 ? `${numRequested} people are interested!` : `we'll let them know!`;

  return (
    <div>
      <Button
        className={props.className}
        disabled={postMutation.isPending || deleteMutation.isPending}
        onClick={() => {
          const newClicked = !isClicked;
          if (!user) {
            setClickedAnonymously(newClicked);
          }
          if (newClicked) {
            postMutation.mutate();
          } else {
            deleteMutation.mutate();
          }
        }}
      >
        <span>{isClicked ? `Thanks, ${interestedText}` : props.text}</span>
      </Button>
    </div>
  );
}

const Button = styled.button`
  ${getButtonBaseStyle()}
  ${getButtonColorStyle({ theme: 'light' })}
`;
